import { type FormEvent, type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  IconAction,
  Input,
  Skeleton,
  Spinner,
} from '@/components/ui';
import {
  useAPIKeys,
  useCreateAPIKey,
  useRevokeAPIKey,
  type APIKeyItem,
  type CreateAPIKeyResponse,
} from '@/hooks/useAPIKeys';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Show-once secret modal
// ---------------------------------------------------------------------------
interface SecretModalProps {
  created: CreateAPIKeyResponse | null;
  onClose: () => void;
}

function SecretModal({ created, onClose }: SecretModalProps): JSX.Element | null {
  const { t } = useTranslation('default');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!created) setCopied(false);
  }, [created]);

  if (!created) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      // Browser denied clipboard access (Safari over http, etc.) — caller
      // can still select+copy manually. We just fail silently.
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[560px] overflow-hidden">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('apikeys_secret_title', { name: created.name })}
          </h2>
        </div>
        <div className="px-5 py-4">
          <div className="px-3 py-2.5 mb-4 bg-warn-bg border border-warn-bd rounded-md text-[13px] text-warn">
            {t('apikeys_secret_warning')}
          </div>

          <label className="block text-[12px] font-medium text-text-2 mb-1.5">
            {t('apikeys_secret_label')}
          </label>
          <div className="flex gap-2">
            <code className="flex-1 px-3 py-2 bg-surface-2 border border-line rounded-md text-[12px] text-text mono break-all">
              {created.secret}
            </code>
            <Button variant="secondary" onClick={() => void copy()}>
              {copied ? t('apikeys_copied') : t('apikeys_copy')}
            </Button>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end">
          <Button onClick={onClose}>{t('apikeys_done')}</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Docs card — outbound link to the static Redoc page (lives outside the SPA
// at /api-docs.html so an external developer can deep-link without auth).
// ---------------------------------------------------------------------------
function DocsCard(): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="lg" className="mb-6 flex items-start gap-4">
      <span
        aria-hidden="true"
        className="flex-shrink-0 w-10 h-10 rounded-md grid place-items-center bg-volt/10 text-volt-600"
      >
        <Icon.code width="20" height="20" />
      </span>
      <div className="flex-1">
        <h2 className="display text-[16px] font-semibold text-text">
          {t('apidocs_title')}
        </h2>
        <p className="text-[13px] text-text-3 mt-1">{t('apidocs_sub')}</p>
      </div>
      <Button
        variant="secondary"
        onClick={() => window.open('/api-docs.html', '_blank', 'noopener')}
      >
        {t('apidocs_open')}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Create form (inline, not modal — simpler for a single field)
// ---------------------------------------------------------------------------
interface CreateFormProps {
  onCreated: (created: CreateAPIKeyResponse) => void;
}

function CreateForm({ onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('default');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateAPIKey();

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('apikeys_err_name_required'));
      return;
    }
    try {
      const k = await create.mutateAsync(name.trim());
      onCreated(k);
      setName('');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : t('apikeys_err_generic'));
    }
  };

  return (
    <Card padding="lg" className="mb-6">
      <h2 className="display text-[16px] font-semibold text-text mb-4">
        {t('apikeys_new_title')}
      </h2>
      {error && (
        <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
          {error}
        </div>
      )}
      <form onSubmit={submit} className="flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-[12px] font-medium text-text-2 mb-1.5" htmlFor="apikey-name">
            {t('apikeys_field_name')}
          </label>
          <Input
            id="apikey-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('apikeys_field_name_placeholder')}
            disabled={create.isPending}
          />
        </div>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? (
            <>
              <Spinner />
              <span>{t('apikeys_creating')}</span>
            </>
          ) : (
            t('apikeys_create')
          )}
        </Button>
      </form>
      <p className="text-[12px] text-text-4 mt-2">{t('apikeys_field_name_help')}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function DevelopersPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const list = useAPIKeys();
  const revoke = useRevokeAPIKey();

  const [created, setCreated] = useState<CreateAPIKeyResponse | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<APIKeyItem | null>(null);

  const items = list.data?.items ?? [];

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revoke.mutateAsync(pendingRevoke.id);
    } finally {
      setPendingRevoke(null);
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('nav_dev')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1">{t('apikeys_sub')}</p>
      </header>

      <DocsCard />

      <CreateForm onCreated={setCreated} />

      <Card padding="none">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('apikeys_table_title')}
          </h2>
          <p className="text-[12px] text-text-3 mt-0.5">
            {t('apikeys_total', { total: items.length.toLocaleString(lang) })}
          </p>
        </div>

        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('apikeys_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('apikeys_empty_sub')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('apikeys_col_name')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apikeys_col_prefix')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apikeys_col_status')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apikeys_col_last_used')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apikeys_col_created')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('apikeys_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => {
                  const revoked = Boolean(k.revoked_at);
                  return (
                    <tr
                      key={k.id}
                      className={cn(
                        'border-b border-line last:border-0 hover:bg-surface-2',
                        revoked && 'opacity-60',
                      )}
                    >
                      <td className="px-5 py-3 text-text font-medium">{k.name}</td>
                      <td className="px-5 py-3 mono text-text-2 whitespace-nowrap">
                        {k.key_prefix}…
                      </td>
                      <td className="px-5 py-3">
                        {revoked ? (
                          <Badge variant="danger">{t('apikeys_status_revoked')}</Badge>
                        ) : (
                          <Badge variant="success">{t('apikeys_status_active')}</Badge>
                        )}
                      </td>
                      <td className="px-5 py-3 text-text-3 whitespace-nowrap">
                        {k.last_used_at ? formatRelativeTime(k.last_used_at, lang) : '—'}
                      </td>
                      <td className="px-5 py-3 text-text-3 whitespace-nowrap">
                        {formatRelativeTime(k.created_at, lang)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {revoked ? (
                          <span className="text-[12px] text-text-4">—</span>
                        ) : (
                          <IconAction
                            icon={Icon.trash}
                            label={t('apikeys_revoke')}
                            onClick={() => setPendingRevoke(k)}
                            disabled={revoke.isPending}
                            tone="danger"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SecretModal created={created} onClose={() => setCreated(null)} />

      <ConfirmModal
        open={pendingRevoke !== null}
        title={t('apikeys_revoke')}
        description={t('apikeys_confirm_revoke', { name: pendingRevoke?.name ?? '' })}
        confirmLabel={t('apikeys_revoke')}
        cancelLabel={t('apikeys_cancel')}
        tone="danger"
        busy={revoke.isPending}
        onConfirm={() => void confirmRevoke()}
        onClose={() => setPendingRevoke(null)}
      />
    </div>
  );
}
